import React, { useState, useEffect, useCallback } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const emptyForm = { 
  key: '', 
  text: '', 
  description: '', 
  is_active: true 
};

export default function AIPromptsPage() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();

  // Fetch prompts from API
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/ai/prompts/');
      setPrompts(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching AI prompts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch AI prompts',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (editingPrompt) {
        // Update existing prompt
        await api.put(`/admin/ai/prompts/${editingPrompt.id}/`, formData);
        setSnackbar({
          open: true,
          message: 'AI prompt updated successfully',
          severity: 'success'
        });
      } else {
        // Create new prompt
        await api.post('/admin/ai/prompts/', formData);
        setSnackbar({
          open: true,
          message: 'AI prompt created successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      fetchPrompts();
    } catch (error) {
      console.error('Error saving AI prompt:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to save AI prompt',
        severity: 'error'
      });
    }
  };

  // Handle delete
  const handleDelete = async (prompt) => {
    if (window.confirm(`Are you sure you want to delete the prompt "${prompt.key}"?`)) {
      try {
        await api.delete(`/admin/ai/prompts/${prompt.id}/`);
        setSnackbar({
          open: true,
          message: 'AI prompt deleted successfully',
          severity: 'success'
        });
        fetchPrompts();
      } catch (error) {
        console.error('Error deleting AI prompt:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete AI prompt',
          severity: 'error'
        });
      }
    }
  };

  // Handle dialog operations
  const handleOpenDialog = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        key: prompt.key,
        text: prompt.text,
        description: prompt.description,
        is_active: prompt.is_active
      });
    } else {
      setEditingPrompt(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrompt(null);
    setFormData(emptyForm);
  };

  // DataGrid columns
  const columns = [
    {
      field: 'key',
      headerName: 'Key',
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 400,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {params.value || 'No description'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'text',
      headerName: 'Text Preview',
      flex: 1,
      minWidth: 500,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontStyle: 'italic',
          color: 'text.secondary'
        }}>
          {params.value ? `${params.value.substring(0, 200)}...` : 'No text'}
        </Typography>
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <AdminLayoutWrapper>
      <Box sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h4" component="h1">
            AI Prompts Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add AI Prompt
          </Button>
        </Box>

        <Box sx={{ 
          width: '100%',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <DataGrid
            rows={prompts}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 100 },
              },
            }}
            sx={{
              width: '100%',
              border: 'none',
              '& .MuiDataGrid-root': {
                width: '100%',
                border: 'none',
              },
              '& .MuiDataGrid-main': {
                width: '100%',
              },
              '& .MuiDataGrid-virtualScroller': {
                width: '100%',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #e0e0e0',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e0e0e0',
              },
            }}
            slots={{
              toolbar: () => (
                <Box sx={{ 
                  p: 1, 
                  display: 'flex', 
                  gap: 1,
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#fafafa'
                }}>
                  <GridToolbarColumnsButton />
                  <GridToolbarDensitySelector />
                  <GridToolbarExport />
                </Box>
              )
            }}
          />
        </Box>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingPrompt ? 'Edit AI Prompt' : 'Add New AI Prompt'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Key"
                value={formData.key}
                onChange={handleInputChange('key')}
                fullWidth
                required
                disabled={!!editingPrompt} // Disable key editing for existing prompts
                helperText="Unique identifier for the prompt (e.g., 'system_prompt', 'difficulty_guidance')"
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                fullWidth
                multiline
                rows={2}
                helperText="Description of what this prompt is used for"
              />
              
              <TextField
                label="Prompt Text"
                value={formData.text}
                onChange={handleInputChange('text')}
                fullWidth
                multiline
                rows={8}
                required
                helperText="The actual prompt text. Can include placeholders like {schema_text}, {difficulty}, etc."
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange('is_active')}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!formData.key || !formData.text}
            >
              {editingPrompt ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayoutWrapper>
  );
}
